import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, ShieldCheck, ShoppingBag, Minus, Plus, Truck } from "lucide-react";
import { api, formatINR } from "@/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import TechCarousel from "@/components/TechCarousel";
import { scopeCss } from "@/lib/scopeCss";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PING_COLOR_CODES = [
  "Maroon — 5° Upright",
  "Silver — 4° Upright",
  "White — 3° Upright",
  "Green — 2° Upright",
  "Blue — 1° Upright",
  "Black — Standard",
  "Red — 1° Flat",
  "Orange — 2° Flat",
  "Brown — 3° Flat",
  "Gold — 4° Flat",
  "Not sure — recommend for me",
];

const LENGTH_OPTIONS = ['-1.5"', '-1"', '-1/2"', 'Standard', '+1/2"', '+1"', '+1.5"'];

// PING grip colour-code sizing dots (drivers/woods/hybrids/wedges)
const GRIP_CODE_META = {
  Blue:   { swatch: "#1E5FBF", size: '-1/16" undersize' },
  Red:    { swatch: "#D0322A", size: '-1/32" undersize' },
  Aqua:   { swatch: "#3AB7C6", size: 'Standard -1/64"' },
  White:  { swatch: "#F5F5F5", size: 'Standard' },
  Gold:   { swatch: "#D4A02C", size: '+1/32" oversize' },
  Orange: { swatch: "#E37820", size: '+1/16" oversize' },
};

const titleCase = (s) => s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function OptionTable({ rows, firstLabel, testid }) {
  if (!rows || rows.length === 0) return <p className="text-muted-foreground text-sm py-6">No options listed.</p>;
  const cols = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b-2 border-[#0A0A0A]">
            {cols.map((c, i) => (
              <th key={c} className="text-left py-3 pr-4 label-caps text-muted-foreground text-[10px]">
                {i === 0 && firstLabel ? firstLabel : titleCase(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border">
              {cols.map((c, j) => (
                <td key={c} className={`py-3 pr-4 ${j === 0 ? "font-semibold" : "text-muted-foreground"}`}>{r[c]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EquipmentDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [recos, setRecos] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({});
  const [customization, setCustomization] = useState("");
  const [notFound, setNotFound] = useState(false);
  // Iron Set Builder state
  const [ironClubs, setIronClubs] = useState([]);
  const [ironMaterial, setIronMaterial] = useState("Steel");
  // Wedge Set Builder state
  const [wedgeLofts, setWedgeLofts] = useState([]);
  const [wedgeMaterial, setWedgeMaterial] = useState("Steel");

  useEffect(() => {
    setProduct(null);
    setActiveImg(0);
    setQty(1);
    setSelected({});
    setCustomization("");
    setIronClubs([]);
    setWedgeLofts([]);
    api.get(`/products/${slug}`)
      .then(({ data }) => {
        if (data.group === "shop") {
          navigate(`/product/${data.slug}`, { replace: true });
          return;
        }
        setProduct(data);
        const defaults = {};
        Object.entries(data.variants || {}).forEach(([k, v]) => { if (v.length === 1) defaults[k] = v[0]; });
        setSelected(defaults);
        // Initialize iron set builder — default preselect 4-PW (drop UW)
        if (data.category_slug === "irons" && (data.iron_steel_per_club || data.iron_graphite_per_club)) {
          const slots = (data.iron_clubs || []).slice();
          setIronClubs(slots.filter((c) => c !== "UW"));
          setIronMaterial(data.iron_steel_per_club ? "Steel" : "Graphite");
        }
        // Initialize wedge set builder — preselect two mid-lofts (54° + 58°) if available
        if (data.category_slug === "wedges" && (data.wedge_steel_per_piece || data.wedge_graphite_per_piece)) {
          const availableLofts = Object.keys(data.loft_grinds || {});
          const preselect = availableLofts.filter((l) => l === "54°" || l === "58°");
          setWedgeLofts(preselect.length ? preselect : availableLofts.slice(0, 1));
          setWedgeMaterial(data.wedge_steel_per_piece ? "Steel" : "Graphite");
        }
        api.get("/products", { params: { group: "equipment", category: data.category_slug } }).then((r) =>
          setRelated(r.data.filter((p) => p.slug !== data.slug).slice(0, 4))
        );
        api.get(`/products/${data.slug}/recommendations`).then((r) => setRecos(r.data)).catch(() => setRecos([]));
      })
      .catch(() => setNotFound(true));
  }, [slug, navigate]);

  if (notFound) {
    return (
      <div className="container-ping py-32 text-center" data-testid="equipment-not-found">
        <h1 className="heading-2">Product Not Found</h1>
        <Link to="/equipment" className="mt-6 inline-block text-[#E31837] label-caps">Back to Equipment</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-ping py-20 grid md:grid-cols-2 gap-12">
        <div className="aspect-square bg-secondary animate-pulse" />
        <div className="space-y-4">
          <div className="h-8 w-2/3 bg-secondary animate-pulse" />
          <div className="h-24 bg-secondary animate-pulse" />
        </div>
      </div>
    );
  }

  const gallery = product.gallery && product.gallery.length ? product.gallery : [product.image];
  const variantKeys = Object.keys(product.variants || {});
  const isIron = product.category_slug === "irons";
  // Chippers/utility clubs live in the "Irons" category on the storefront
  // but don't need lie-angle + length fitting. Admin can toggle this off
  // per product from AdminProductForm.
  const showLieLength = isIron && product.iron_show_lie_length !== false;

  // Shaft → dependent Flex options
  const shafts = product.shafts || [];
  const hasShaftPicker = shafts.length > 0;
  const selectedShaft = shafts.find((s) => s.name === selected["Shaft"]);
  const shaftFlexes = selectedShaft?.flexes || [];

  // Wedge Loft → dependent Grind matrix
  const loftGrinds = product.loft_grinds || {};
  const hasLoftGrindMatrix = Object.keys(loftGrinds).length > 0;
  const loftOptions = hasLoftGrindMatrix ? Object.keys(loftGrinds) : [];
  const grindOptions = hasLoftGrindMatrix && selected["Loft"] ? (loftGrinds[selected["Loft"]] || []) : [];

  // Wedge / other Finish options
  const finishOptions = product.finishes || [];

  // Grip colour-code (size) options — dots for drivers/woods/hybrids/wedges
  const gripCodes = (product.grip_color_codes || []).filter((c) => GRIP_CODE_META[c]);

  const gripNames = (product.grips || []).map((g) => g.name).filter(Boolean);

  // Iron Set Builder
  const ironSlots = product.iron_clubs || [];
  const perClubSteel = product.iron_steel_per_club || 0;
  const perClubGraphite = product.iron_graphite_per_club || 0;
  const hasBothMaterials = perClubSteel > 0 && perClubGraphite > 0;
  const isIronBuilder = isIron && ironSlots.length > 0 && (perClubSteel > 0 || perClubGraphite > 0);
  const perClubPrice = ironMaterial === "Steel" ? perClubSteel : perClubGraphite;
  const MIN_IRON_CLUBS = 3;
  const ironTotalPrice = isIronBuilder ? ironClubs.length * perClubPrice : 0;
  const ironBelowMin = isIronBuilder && ironClubs.length < MIN_IRON_CLUBS;

  // Wedge Set Builder
  const isWedge = product.category_slug === "wedges";
  const perPieceSteel = product.wedge_steel_per_piece || 0;
  const perPieceGraphite = product.wedge_graphite_per_piece || 0;
  const isWedgeBuilder = isWedge && hasLoftGrindMatrix && (perPieceSteel > 0 || perPieceGraphite > 0);
  const hasBothWedgeMaterials = perPieceSteel > 0 && perPieceGraphite > 0;
  const perPiecePrice = wedgeMaterial === "Steel" ? perPieceSteel : perPieceGraphite;
  const wedgeTotalPrice = isWedgeBuilder ? wedgeLofts.length * perPiecePrice : 0;
  const wedgeBelowMin = isWedgeBuilder && wedgeLofts.length < 1;
  // Intersect grinds across selected lofts so only shared grinds appear
  const wedgeCommonGrinds = isWedgeBuilder && wedgeLofts.length > 0
    ? wedgeLofts.reduce((acc, l, idx) => {
        const g = new Set(loftGrinds[l] || []);
        return idx === 0 ? g : new Set([...acc].filter((x) => g.has(x)));
      }, new Set())
    : new Set();
  const grindOptionsForWedgeSet = Array.from(wedgeCommonGrinds);

  const displayPrice = isIronBuilder ? ironTotalPrice : (isWedgeBuilder ? wedgeTotalPrice : product.price);

  // If iron builder is active, filter shaft dropdown by material (steel vs graphite)
  const shaftsForDisplay = isIronBuilder
    ? shafts.filter((s) => {
        const n = (s.name || "").toLowerCase();
        if (ironMaterial === "Steel") return n.includes("steel") || (!n.includes("graphite"));
        return n.includes("graphite");
      })
    : (isWedgeBuilder
      ? shafts.filter((s) => {
          const n = (s.name || "").toLowerCase();
          if (wedgeMaterial === "Steel") return n.includes("steel") || (!n.includes("graphite"));
          return n.includes("graphite");
        })
      : shafts);

  // Required-fields tracker (drives the "all selected" check)
  const required = [];
  variantKeys.forEach((k) => required.push(k));
  if (hasLoftGrindMatrix && !isWedgeBuilder) { required.push("Loft"); required.push("Grind"); }
  if (isWedgeBuilder) required.push("Grind");
  if (finishOptions.length) required.push("Finish");
  if (hasShaftPicker) { required.push("Shaft"); if (shaftFlexes.length) required.push("Flex"); }
  if (gripNames.length) required.push("Grip");
  if (gripCodes.length) required.push("Grip Size");
  if (showLieLength) { required.push("Lie Angle"); required.push("Length"); }
  const allSelected = required.every((k) => selected[k]) && !ironBelowMin && !wedgeBelowMin;

  const hasSpecs = Object.keys(product.specs || {}).length > 0 || !!product.spec_html || !!product.spec_image;
  const hasShafts = shafts.length > 0;
  const hasGrips = (product.grips || []).length > 0;
  const defaultTab = hasSpecs ? "specs" : hasShafts ? "shafts" : hasGrips ? "grips" : "overview";

  // Reset dependent picks when a parent changes
  const pick = (key, value) => setSelected((s) => {
    const next = { ...s, [key]: value };
    if (key === "Shaft") delete next.Flex;
    if (key === "Loft") delete next.Grind;
    return next;
  });

  // When the iron material toggles, clear any picked shaft/flex so the user
  // re-picks from the filtered list.
  const changeIronMaterial = (m) => {
    setIronMaterial(m);
    setSelected((s) => {
      const next = { ...s };
      delete next.Shaft;
      delete next.Flex;
      return next;
    });
  };

  const toggleClub = (c) => setIronClubs((arr) => arr.includes(c) ? arr.filter((x) => x !== c) : [...arr, c]);

  // Wedge builder handlers
  const changeWedgeMaterial = (m) => {
    setWedgeMaterial(m);
    setSelected((s) => {
      const next = { ...s };
      delete next.Shaft;
      delete next.Flex;
      return next;
    });
  };
  const toggleWedgeLoft = (l) => setWedgeLofts((arr) => {
    const next = arr.includes(l) ? arr.filter((x) => x !== l) : [...arr, l];
    // If current Grind is no longer available for all selected lofts, clear it
    if (selected.Grind && next.length > 0) {
      const stillValid = next.every((lft) => (loftGrinds[lft] || []).includes(selected.Grind));
      if (!stillValid) setSelected((s) => { const n = { ...s }; delete n.Grind; return n; });
    }
    return next;
  });

  const handleAdd = () => {
    if (ironBelowMin) {
      toast.error(`Select at least ${MIN_IRON_CLUBS} clubs`);
      return;
    }
    if (wedgeBelowMin) {
      toast.error("Select at least 1 wedge loft");
      return;
    }
    if (!allSelected) {
      toast.error("Please select all options");
      return;
    }
    const parts = required.map((k) => `${k}: ${selected[k]}`);
    if (isIronBuilder) {
      const ordered = ironSlots.filter((c) => ironClubs.includes(c));
      parts.unshift(`Material: ${ironMaterial}`);
      parts.unshift(`Set (${ordered.length} clubs): ${ordered.join(", ")}`);
    }
    if (isWedgeBuilder) {
      // Order lofts by the original loft_grinds key order (ascending)
      const allLofts = Object.keys(loftGrinds);
      const ordered = allLofts.filter((l) => wedgeLofts.includes(l));
      parts.unshift(`Material: ${wedgeMaterial}`);
      parts.unshift(`Set (${ordered.length} wedges): ${ordered.join(", ")}`);
    }
    const variantStr = parts.join(" / ") || null;
    const priceOverride = isIronBuilder ? ironTotalPrice : (isWedgeBuilder ? wedgeTotalPrice : null);
    addItem(product, qty, variantStr, customization.trim() || null, priceOverride);
  };

  return (
    <div data-testid="equipment-detail-page">
      <div className="container-ping pt-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 label-caps text-muted-foreground hover:text-[#0A0A0A] btn-transition" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      </div>

      <div className="container-ping py-8 grid md:grid-cols-2 gap-12">
        {/* Image gallery */}
        <div className="md:sticky md:top-28 self-start">
          <div className="relative bg-[#0A0A0A] aspect-square overflow-hidden border border-border">
            {product.tag && (
              <span className="absolute top-4 left-4 z-10 label-caps px-3 py-1 bg-[#E31837] text-white">{product.tag}</span>
            )}
            <img src={gallery[activeImg]} alt={product.name} className="w-full h-full knockout-white" style={{ objectFit: product.image_fit || "cover", objectPosition: product.image_position || "center" }} data-testid="equipment-image" />
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2" data-testid="gallery-thumbs">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square bg-[#0A0A0A] overflow-hidden border-2 btn-transition ${i === activeImg ? "border-[#E31837]" : "border-transparent hover:border-border"}`}
                  data-testid={`gallery-thumb-${i}`}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover knockout-white" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info + purchase */}
        <div>
          <p className="label-caps text-[#E31837]">{product.category}</p>
          <h1 className="heading-2 mt-2" data-testid="equipment-name">{product.name}</h1>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-3xl font-bold" data-testid="equipment-price">{formatINR(displayPrice)}</span>
            {product.old_price && <span className="text-lg text-muted-foreground line-through">{formatINR(product.old_price)}</span>}
            {isIronBuilder && (
              <span className="text-xs text-muted-foreground ml-2" data-testid="iron-price-breakdown">
                {ironClubs.length} × ₹{perClubPrice.toLocaleString("en-IN")} ({ironMaterial})
              </span>
            )}
            {isWedgeBuilder && (
              <span className="text-xs text-muted-foreground ml-2" data-testid="wedge-price-breakdown">
                {wedgeLofts.length} × ₹{perPiecePrice.toLocaleString("en-IN")} ({wedgeMaterial})
              </span>
            )}
          </div>
          <p className="mt-6 text-muted-foreground leading-relaxed">{product.description}</p>

          {/* Iron Set Builder — chip grid + material toggle */}
          {isIronBuilder && (
            <div className="mt-8 border border-border p-5" data-testid="iron-set-builder">
              <div className="flex items-baseline justify-between">
                <p className="label-caps">Build Your Set</p>
                <p className="text-xs text-muted-foreground">Min {MIN_IRON_CLUBS} clubs</p>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Tap the clubs you want. Price updates as you build.</p>

              <div className="mt-4 flex flex-wrap gap-2" data-testid="iron-clubs-row">
                {ironSlots.map((c) => {
                  const active = ironClubs.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleClub(c)}
                      className={`min-w-[52px] px-3 py-2 text-sm font-semibold border btn-transition ${active ? "border-[#E31837] bg-[#E31837] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                      data-testid={`iron-club-${c}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>

              {hasBothMaterials && (
                <div className="mt-5">
                  <p className="label-caps text-muted-foreground mb-2">Shaft Material</p>
                  <div className="grid grid-cols-2 gap-2 max-w-xs">
                    {["Steel", "Graphite"].map((m) => {
                      const price = m === "Steel" ? perClubSteel : perClubGraphite;
                      const active = ironMaterial === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => changeIronMaterial(m)}
                          className={`px-4 py-3 border text-sm font-semibold btn-transition text-left ${active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                          data-testid={`iron-material-${m.toLowerCase()}`}
                        >
                          <span className="block">{m}</span>
                          <span className={`block text-xs mt-0.5 ${active ? "text-white/70" : "text-muted-foreground"}`}>₹{price.toLocaleString("en-IN")}/club</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-baseline justify-between border-t border-border pt-4">
                <p className="label-caps text-muted-foreground">Set Total</p>
                <p className="text-2xl font-bold" data-testid="iron-set-total">{formatINR(ironTotalPrice)}</p>
              </div>
              {ironBelowMin && (
                <p className="mt-2 text-xs text-[#E31837]" data-testid="iron-min-warning">Please choose at least {MIN_IRON_CLUBS} clubs to continue.</p>
              )}
            </div>
          )}

          {/* Simple variant selectors (Loft/Hand/Set/etc.) — hides Loft when a loft-grind matrix is set */}
          {variantKeys.filter((k) => !(hasLoftGrindMatrix && k === "Loft")).map((k) => (
            <div key={k} className="mt-6" data-testid={`variant-group-${k.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="label-caps text-muted-foreground mb-2">{k}</p>
              <div className="flex flex-wrap gap-2">
                {product.variants[k].map((v) => (
                  <button
                    key={v}
                    onClick={() => pick(k, v)}
                    className={`px-4 py-2 border text-sm font-semibold btn-transition ${selected[k] === v ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                    data-testid={`variant-${k.toLowerCase().replace(/\s/g, "-")}-${v.replace(/[^a-zA-Z0-9]/g, "")}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Wedge Set Builder — chip grid + material toggle */}
          {isWedgeBuilder && (
            <div className="mt-8 border border-border p-5" data-testid="wedge-set-builder">
              <div className="flex items-baseline justify-between">
                <p className="label-caps">Build Your Wedge Set</p>
                <p className="text-xs text-muted-foreground">Pick 1+ lofts</p>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Tap the lofts you want. Price updates as you build. Grind &amp; shaft picked below apply to every wedge in the set.</p>

              <div className="mt-4 flex flex-wrap gap-2" data-testid="wedge-lofts-row">
                {Object.keys(loftGrinds).map((l) => {
                  const active = wedgeLofts.includes(l);
                  return (
                    <button
                      key={l}
                      type="button"
                      onClick={() => toggleWedgeLoft(l)}
                      className={`min-w-[56px] px-3 py-2 text-sm font-semibold border btn-transition ${active ? "border-[#E31837] bg-[#E31837] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                      data-testid={`wedge-loft-${l.replace(/[^a-zA-Z0-9]/g, "")}`}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>

              {hasBothWedgeMaterials && (
                <div className="mt-5">
                  <p className="label-caps text-muted-foreground mb-2">Shaft Material</p>
                  <div className="grid grid-cols-2 gap-2 max-w-xs">
                    {["Steel", "Graphite"].map((m) => {
                      const price = m === "Steel" ? perPieceSteel : perPieceGraphite;
                      const active = wedgeMaterial === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => changeWedgeMaterial(m)}
                          className={`px-4 py-3 border text-sm font-semibold btn-transition text-left ${active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                          data-testid={`wedge-material-${m.toLowerCase()}`}
                        >
                          <span className="block">{m}</span>
                          <span className={`block text-xs mt-0.5 ${active ? "text-white/70" : "text-muted-foreground"}`}>₹{price.toLocaleString("en-IN")}/piece</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-baseline justify-between border-t border-border pt-4">
                <p className="label-caps text-muted-foreground">Set Total</p>
                <p className="text-2xl font-bold" data-testid="wedge-set-total">{formatINR(wedgeTotalPrice)}</p>
              </div>
              {wedgeBelowMin && (
                <p className="mt-2 text-xs text-[#E31837]" data-testid="wedge-min-warning">Please select at least one wedge loft.</p>
              )}
            </div>
          )}

          {/* Wedge Loft → Grind dependent matrix (single-piece mode only) */}
          {hasLoftGrindMatrix && !isWedgeBuilder && (
            <>
              <div className="mt-6" data-testid="variant-group-loft">
                <p className="label-caps text-muted-foreground mb-2">Loft</p>
                <div className="flex flex-wrap gap-2">
                  {loftOptions.map((lo) => (
                    <button
                      key={lo}
                      onClick={() => pick("Loft", lo)}
                      className={`px-4 py-2 border text-sm font-semibold btn-transition ${selected.Loft === lo ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                      data-testid={`loft-${lo.replace(/[^a-zA-Z0-9]/g, "")}`}
                    >
                      {lo}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-6" data-testid="variant-group-grind">
                <p className="label-caps text-muted-foreground mb-2">Grind {!selected.Loft && <span className="text-[#E31837] normal-case tracking-normal text-xs">— pick a loft first</span>}</p>
                <div className="flex flex-wrap gap-2">
                  {(selected.Loft ? grindOptions : []).map((gr) => (
                    <button
                      key={gr}
                      onClick={() => pick("Grind", gr)}
                      className={`px-4 py-2 border text-sm font-semibold btn-transition ${selected.Grind === gr ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                      data-testid={`grind-${gr.replace(/[^a-zA-Z0-9]/g, "")}`}
                    >
                      {gr}
                    </button>
                  ))}
                  {!selected.Loft && <p className="text-xs text-muted-foreground py-2">Grind options unlock after a loft is chosen.</p>}
                </div>
              </div>
            </>
          )}

          {/* Grind selector for the Wedge Set Builder (single grind applied to every loft in the set) */}
          {isWedgeBuilder && (
            <div className="mt-6" data-testid="variant-group-grind">
              <p className="label-caps text-muted-foreground mb-2">
                Grind {wedgeLofts.length === 0 && <span className="text-[#E31837] normal-case tracking-normal text-xs">— pick a loft first</span>}
                {wedgeLofts.length > 0 && grindOptionsForWedgeSet.length === 0 && <span className="text-[#E31837] normal-case tracking-normal text-xs">— no grind common to all selected lofts</span>}
              </p>
              <div className="flex flex-wrap gap-2">
                {grindOptionsForWedgeSet.map((gr) => (
                  <button
                    key={gr}
                    onClick={() => pick("Grind", gr)}
                    className={`px-4 py-2 border text-sm font-semibold btn-transition ${selected.Grind === gr ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                    data-testid={`grind-${gr.replace(/[^a-zA-Z0-9]/g, "")}`}
                  >
                    {gr}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Only grinds available on <em>every</em> selected loft are shown.</p>
            </div>
          )}

          {/* Finish (e.g. Chrome / Midnight) */}
          {finishOptions.length > 0 && (
            <div className="mt-6" data-testid="variant-group-finish">
              <p className="label-caps text-muted-foreground mb-2">Finish</p>
              <div className="flex flex-wrap gap-2">
                {finishOptions.map((f) => (
                  <button
                    key={f}
                    onClick={() => pick("Finish", f)}
                    className={`px-4 py-2 border text-sm font-semibold btn-transition ${selected.Finish === f ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                    data-testid={`finish-${f.replace(/[^a-zA-Z0-9]/g, "")}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Shaft (dependent Flex) */}
          {hasShaftPicker && (
            <>
              <div className="mt-6" data-testid="custom-group-shaft">
                <p className="label-caps text-muted-foreground mb-2">Shaft{isIronBuilder ? ` — ${ironMaterial}` : (isWedgeBuilder ? ` — ${wedgeMaterial}` : "")}</p>
                <Select value={selected.Shaft || ""} onValueChange={(v) => pick("Shaft", v)}>
                  <SelectTrigger className="w-full rounded-none border-border h-12" data-testid="custom-select-shaft">
                    <SelectValue placeholder={shaftsForDisplay.length ? "Select Shaft" : `No ${(isWedgeBuilder ? wedgeMaterial : ironMaterial).toLowerCase()} shaft configured`} />
                  </SelectTrigger>
                  <SelectContent>
                    {shaftsForDisplay.map((s) => (
                      <SelectItem key={s.name} value={s.name} data-testid={`custom-option-shaft-${s.name.replace(/[^a-zA-Z0-9]/g, "")}`}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {shaftFlexes.length > 0 && (
                <div className="mt-6" data-testid="custom-group-flex">
                  <p className="label-caps text-muted-foreground mb-2">Flex</p>
                  <div className="flex flex-wrap gap-2">
                    {shaftFlexes.map((fl) => (
                      <button
                        key={fl}
                        onClick={() => pick("Flex", fl)}
                        className={`px-4 py-2 border text-sm font-semibold btn-transition ${selected.Flex === fl ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                        data-testid={`flex-${fl.replace(/[^a-zA-Z0-9]/g, "")}`}
                      >
                        {fl}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Flexes shown are the ones offered on the selected shaft.</p>
                </div>
              )}
            </>
          )}

          {/* Grip name */}
          {gripNames.length > 0 && (
            <div className="mt-6" data-testid="custom-group-grip">
              <p className="label-caps text-muted-foreground mb-2">Grip</p>
              <Select value={selected.Grip || ""} onValueChange={(v) => pick("Grip", v)}>
                <SelectTrigger className="w-full rounded-none border-border h-12" data-testid="custom-select-grip">
                  <SelectValue placeholder="Select Grip" />
                </SelectTrigger>
                <SelectContent>
                  {gripNames.map((g) => (
                    <SelectItem key={g} value={g} data-testid={`custom-option-grip-${g.replace(/[^a-zA-Z0-9]/g, "")}`}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Grip Colour Code (size) — dot swatches with tooltip */}
          {gripCodes.length > 0 && (
            <div className="mt-6" data-testid="grip-code-group">
              <p className="label-caps text-muted-foreground mb-2">Grip Size — PING Colour Code</p>
              <div className="flex flex-wrap gap-3">
                {gripCodes.map((code) => {
                  const meta = GRIP_CODE_META[code];
                  const active = selected["Grip Size"] === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => pick("Grip Size", code)}
                      title={`${code} — ${meta.size}`}
                      aria-label={`${code} grip (${meta.size})`}
                      className={`group relative h-11 w-11 rounded-full border-2 btn-transition ${active ? "border-[#0A0A0A] scale-110" : "border-border hover:border-[#0A0A0A]"}`}
                      style={{ backgroundColor: meta.swatch }}
                      data-testid={`grip-code-${code.toLowerCase()}`}
                    >
                      {active && <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold" style={{ color: code === "White" ? "#0A0A0A" : "#fff" }}>✓</span>}
                    </button>
                  );
                })}
              </div>
              {selected["Grip Size"] && (
                <p className="mt-2 text-xs text-muted-foreground">
                  <span className="font-semibold text-[#0A0A0A]">{selected["Grip Size"]}</span> · {GRIP_CODE_META[selected["Grip Size"]].size}
                </p>
              )}
              {!selected["Grip Size"] && (
                <p className="mt-2 text-xs text-muted-foreground">Hover a dot to see its size. Not sure? Book a fitting — our specialists will size you up.</p>
              )}
            </div>
          )}

          {/* Iron-only: Lie Angle + Length (skipped for chippers/utility clubs
              where admin has turned iron_show_lie_length OFF) */}
          {showLieLength && (
            <>
              <div className="mt-6" data-testid="custom-group-lie-angle">
                <p className="label-caps text-muted-foreground mb-2">Lie Angle</p>
                <Select value={selected["Lie Angle"] || ""} onValueChange={(v) => pick("Lie Angle", v)}>
                  <SelectTrigger className="w-full rounded-none border-border h-12" data-testid="custom-select-lie-angle">
                    <SelectValue placeholder="Select Lie Angle" />
                  </SelectTrigger>
                  <SelectContent>
                    {PING_COLOR_CODES.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-2 text-xs text-muted-foreground">PING color-code fitting system. Not sure? Choose &ldquo;recommend for me&rdquo; and our fitters will advise.</p>
              </div>
              <div className="mt-6" data-testid="custom-group-length">
                <p className="label-caps text-muted-foreground mb-2">Length</p>
                <Select value={selected["Length"] || ""} onValueChange={(v) => pick("Length", v)}>
                  <SelectTrigger className="w-full rounded-none border-border h-12" data-testid="custom-select-length">
                    <SelectValue placeholder="Select Length" />
                  </SelectTrigger>
                  <SelectContent>
                    {LENGTH_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Further customisation */}
          {(isIron || variantKeys.length > 0 || hasShaftPicker || gripCodes.length > 0 || finishOptions.length > 0) && (
            <div className="mt-6" data-testid="customization-group">
              <p className="label-caps text-muted-foreground mb-2">Any Further Customisation Required</p>
              <Textarea
                value={customization}
                onChange={(e) => setCustomization(e.target.value.slice(0, 120))}
                placeholder="Type something (e.g. initials, stamping, special request)"
                className="rounded-none border-border resize-none"
                rows={2}
                data-testid="customization-input"
              />
              <p className="mt-1 text-xs text-muted-foreground text-right">{customization.length}/120</p>
            </div>
          )}

          {/* Qty + Add */}
          <div className="mt-8 flex items-stretch gap-4">
            <div className="flex items-center border border-[#0A0A0A]">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-4 hover:bg-secondary btn-transition" data-testid="qty-decrease" aria-label="Decrease">
                <Minus className="h-4 w-4" />
              </button>
              <span className="px-5 font-bold w-12 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-4 py-4 hover:bg-secondary btn-transition" data-testid="qty-increase" aria-label="Increase">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={handleAdd}
              className="flex-1 bg-[#E31837] text-white label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-[0.98]"
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag className="h-5 w-5" /> Add To Bag
            </button>
          </div>

          <div className="mt-6 flex flex-col gap-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#E31837]" /> Dispatched in 2–4 business days</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#E31837]" /> 100% genuine · Full manufacturer warranty</span>
            <Link to="/dealers" className="flex items-center gap-2 hover:text-[#0A0A0A] btn-transition" data-testid="find-fitting-btn">
              <MapPin className="h-4 w-4 text-[#E31837]" /> Prefer a custom fit? Find a fitting studio
            </Link>
          </div>
        </div>
      </div>

      {/* Technology carousel */}
      <TechCarousel slides={product.tech_slides} />

      {/* Detail tabs */}
      <div className="container-ping pb-16">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="w-full justify-start rounded-none bg-transparent border-b border-border h-auto p-0 gap-0 flex-wrap" data-testid="detail-tabs">
            {hasSpecs && <TabsTrigger value="specs" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#E31837] data-[state=active]:shadow-none px-3 md:px-5 py-3 text-xs md:text-sm" data-testid="tab-specs">Key Specifications</TabsTrigger>}
            {hasShafts && <TabsTrigger value="shafts" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#E31837] data-[state=active]:shadow-none px-3 md:px-5 py-3 text-xs md:text-sm" data-testid="tab-shafts">Shafts</TabsTrigger>}
            {hasGrips && <TabsTrigger value="grips" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#E31837] data-[state=active]:shadow-none px-3 md:px-5 py-3 text-xs md:text-sm" data-testid="tab-grips">Grips</TabsTrigger>}
            <TabsTrigger value="overview" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#E31837] data-[state=active]:shadow-none px-3 md:px-5 py-3 text-xs md:text-sm" data-testid="tab-overview">Overview</TabsTrigger>
          </TabsList>

          {hasSpecs && (
            <TabsContent value="specs" className="pt-8" data-testid="content-specs">
              {/* Priority: custom HTML/CSS block → uploaded image → fall back to specs key/value table */}
              {product.spec_html ? (
                <div id={`spec-block-${product.id}`} data-testid="spec-html-block">
                  {product.spec_css && (
                    <style dangerouslySetInnerHTML={{ __html: scopeCss(product.spec_css, `spec-block-${product.id}`) }} />
                  )}
                  <div dangerouslySetInnerHTML={{ __html: product.spec_html }} />
                </div>
              ) : product.spec_image ? (
                <div className={`w-full bg-[#F4F4F5] border border-border overflow-hidden ${product.category_slug === "wedges" ? "aspect-[4/5] max-w-md" : "aspect-[16/6]"}`} data-testid="spec-image-block">
                  <img
                    src={product.spec_image}
                    alt={`${product.name} — Key Specifications`}
                    className="w-full h-full"
                    style={{ objectFit: product.spec_image_fit || "cover", objectPosition: product.spec_image_position || "center" }}
                    data-testid="spec-image"
                  />
                </div>
              ) : (
                <div className="max-w-2xl">
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(product.specs).map(([k, v]) => (
                        <tr key={k} className="border-b border-border">
                          <td className="py-3 font-semibold uppercase text-xs tracking-wider text-muted-foreground w-1/3">{k}</td>
                          <td className="py-3">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          )}
          {hasShafts && (
            <TabsContent value="shafts" className="pt-8" data-testid="content-shafts">
              <OptionTable rows={product.shafts} firstLabel="Shaft" testid="shafts-table" />
            </TabsContent>
          )}
          {hasGrips && (
            <TabsContent value="grips" className="pt-8" data-testid="content-grips">
              <OptionTable rows={product.grips} firstLabel="Grip" testid="grips-table" />
            </TabsContent>
          )}
          <TabsContent value="overview" className="pt-8" data-testid="content-overview">
            <p className="max-w-2xl text-muted-foreground leading-relaxed">{product.description}</p>
          </TabsContent>
        </Tabs>
      </div>

      {related.length > 0 && (
        <section className="bg-[#F4F4F5] py-16">
          <div className="container-ping">
            <h2 className="heading-3 mb-8">More In {product.category}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {recos.length > 0 && (
        <section className="bg-white py-16 border-t border-border" data-testid="recommendations">
          <div className="container-ping">
            <p className="label-caps text-[#E31837] mb-2">Complete The Bag</p>
            <h2 className="heading-3 mb-8">Pairs Well With Your {product.category}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {recos.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
